import {
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { SciChartSurface } from 'scichart/Charting/Visuals/SciChartSurface';
import { NumericAxis } from 'scichart/Charting/Visuals/Axis/NumericAxis';
import { XyDataSeries } from 'scichart/Charting/Model/XyDataSeries';
import { FastLineRenderableSeries } from 'scichart/Charting/Visuals/RenderableSeries/FastLineRenderableSeries';
import { LossDataRow } from '../models/loss-data-row';
import { ProjectService } from '../services/project.service';
import { ENumericFormat } from 'scichart/types/NumericFormat';
import { EAutoRange } from 'scichart/types/AutoRange';
import { EAxisAlignment } from 'scichart/types/AxisAlignment';
import { NumberRange } from 'scichart/Core/NumberRange';
import { CursorModifier } from 'scichart/Charting/ChartModifiers/CursorModifier';

import {
  BoxAnnotation,
  EAnnotationType,
  ELabelPlacement,
  HeatmapColorMap,
  RolloverModifier,
  RubberBandXyZoomModifier,
  TSciChart,
  TextAnnotation,
  UniformHeatmapDataSeries,
  UniformHeatmapRenderableSeries,
  VerticalLineAnnotation,
  ZoomPanModifier,
  zeroArray2D,
} from 'scichart';
import { Subscription, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FiberFault } from '../models/fiber-fault';
import { ProjectLossData } from '../models/project-loss-data';

@Component({
  selector: 'app-health',
  templateUrl: './health.component.html',
  styleUrls: ['./health.component.scss'],
})
export class HealthComponent implements OnInit, OnChanges, OnDestroy {
  currentTab: number = 1;
  errorMessage = '';
  lossDataLoaded: boolean = false;
  mainFaultType: number = 0;
  cmsFaultType: number = 0;

  // Temp Chart
  lossData: ProjectLossData = {} as ProjectLossData;
  lossDataRows: LossDataRow[] = [];
  xyDataSeries: XyDataSeries = {} as XyDataSeries;
  tick: number = 0;
  sciChartSurface?: SciChartSurface; // Store reference to chart surface
  isInitialLoad: boolean = true; // Track if this is the first load
  FD: FiberFault[] = [];
  FP: FiberFault[] = [];
  FM: FiberFault[] = [];
  defaultFaults: FiberFault[] = [
    {
      D: 'OK',
      FT: '0',
      P: 0,
      SI: 1,
      V: 0,
      PS: 0,
      PE: 0,
    },
  ];

  channelrange_start: number = 0;
  channelrange_end: number = 4;
  channelrange_span: number = 4;
  samplingInterval: number = 1;
  heatMaps: number[][][] = [];
  isRunning: boolean = false;

  rolloverModifier?: RolloverModifier;
  zoomPanModifier?: ZoomPanModifier;
  rubberBandZoomModifier?: RubberBandXyZoomModifier;

  // Chart Controls Events
  public onZoomOut?: () => void;
  public onAddMarker?: () => void;

  selectedProjectSubscription: Subscription = {} as Subscription;
  tempDataSubscription: Subscription = {} as Subscription;
  tempRTDataSubscription: Subscription = {} as Subscription;
  tickSubscription: Subscription = {} as Subscription;

  faultBoxAnnotation: BoxAnnotation = {} as BoxAnnotation;

  constructor(public projectService: ProjectService) {}

  ngOnInit() {
    this.initSciChart();
    this.setupProjectSubscription();
    this.createLossGraph();

    this.projectService.isHelpOverlayVisible = false;
  }

  ngOnDestroy(): void {
    // Dispose of the chart surface to free up resources
    if (this.sciChartSurface) {
      this.sciChartSurface.delete();
    }
    
    this.selectedProjectSubscription.unsubscribe();
    this.tempDataSubscription.unsubscribe();
    this.tempRTDataSubscription.unsubscribe();
    //this.tickSubscription.unsubscribe();
  }

  // Setup subscription to recreate chart when project changes
  setupProjectSubscription() {
    this.selectedProjectSubscription =
      this.projectService.selectedProject.subscribe(
        (projectName) => {
          if (projectName && projectName.length > 0) {
            console.log('Health > Project changed to:', projectName);
            console.log('Health > Current unit setting:', this.projectService.isMetricUnit ? 'Metric' : 'Imperial');
            
            // Skip recreation on initial load (graph is already being created in ngOnInit)
            if (this.isInitialLoad) {
              this.isInitialLoad = false;
              console.log('Health > Skipping recreation on initial load');
              return;
            }
            
            // Recreate the graph with the new project's unit settings
            this.recreateLossGraph();
          }
        },
        (error) => (this.errorMessage = error)
      );
  }

  // Recreate the loss graph (called when project changes)
  async recreateLossGraph() {
    console.log('Health > Recreating graph with units:', this.projectService.isMetricUnit ? 'Metric' : 'Imperial');
    
    // Dispose of the existing chart if it exists
    if (this.sciChartSurface) {
      console.log('Health > Disposing existing chart');
      this.sciChartSurface.delete();
      this.sciChartSurface = undefined;
    }

    // Clear data
    this.heatMaps = [];
    this.FD = [];
    this.FP = [];
    this.FM = [];

    // Recreate the chart
    await this.createLossGraph();
  }

  selectTab(tabNo: number) {
    this.currentTab = tabNo;
  }

  ngOnChanges(changes: SimpleChanges) {
    console.log(this.currentTab);
  }

  async initSciChart() {
    SciChartSurface.useWasmFromCDN();
    SciChartSurface.setRuntimeLicenseKey(environment.sciChartKey);
  }

  // Loss Graph ===========================================================================

  async createLossGraph() {
    console.log('Health > Creating graph with units:', this.projectService.isMetricUnit ? 'Metric (m)' : 'Imperial (ft)');
    
    // Create the SciChartSurface in the div 'scichart-root'
    const { sciChartSurface, wasmContext } = await SciChartSurface.create(
      'loss-graph'
    );

    // Store reference to the chart surface
    this.sciChartSurface = sciChartSurface;

    //sciChartSurface.load

    // Create an X,Y Axis and add to the chart
    const xAxis = new NumericAxis(wasmContext, {
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit ? 'm' : 'ft',
    });

    // TODO: Set in VericureBlazor Code but not working
    //xAxis.labelProvider.formatLabel = this.formatxAxisTemperatureGraph;

    const yAxis = new NumericAxis(wasmContext, {
      //autoRange: EAutoRange.Always,
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: 'dB',
    });
    yAxis.axisAlignment = EAxisAlignment.Left;
    //yAxis.growBy = new NumberRange(0.2, 0.2);
    //xAxis.visibleRange = new NumberRange(0, 100);

    sciChartSurface.xAxes.add(xAxis);
    sciChartSurface.yAxes.add(yAxis);
    const cursorModifier = new CursorModifier({
      crosshairStroke: '#ff6600',
      crosshairStrokeThickness: 1,
      tooltipContainerBackground: '#000',
      tooltipTextStroke: '#ff6600',
      showTooltip: true,
      axisLabelFill: '#b36200',
      axisLabelStroke: '#fff',
      //showTooltip: false,   // TODO: Not Available
      showYLine: false,
    });

    //sciChartSurface.chartModifiers.add(cursorModifier);
    //sciChartSurface.chartModifiers.add(rolloverModifier);
    //sciChartSurface.chartModifiers.add(mouseWheelZoomModifier);
    //sciChartSurface.chartModifiers.add(zoomExtentsModifier);
    //sciChartSurface.chartModifiers.add(rubberBandZoomModifier);

    // Vertical Line annotation
    const ann_t1 = new VerticalLineAnnotation({
      labelPlacement: ELabelPlacement.Axis,
      showLabel: true,
      stroke: '#7F30F3',
      strokeThickness: 3,
      x1: this.projectService.getVerticalAnnotationPosition(),
      axisLabelFill: '#7F30F3',
      axisFontSize: 14,
      isEditable: true,
      onDrag: (args) => {
        this.projectService.setVerticalAnnotationPosition(
          +ann_t1.x1.toFixed(2)
        );
      },
    });

    //sciChartSurface.annotations.add(ann_t1);

    this.rolloverModifier = new RolloverModifier();
    this.rolloverModifier.rolloverLineStroke = '#41B972';
    this.rolloverModifier.rolloverLineStrokeThickness = 2;
    this.rolloverModifier.showRolloverLine = true;
    sciChartSurface.chartModifiers.add(this.rolloverModifier);

    // Create new modifier instances to avoid "already attached" errors
    this.rubberBandZoomModifier = new RubberBandXyZoomModifier();
    this.rubberBandZoomModifier.isEnabled = false;
    sciChartSurface.chartModifiers.add(this.rubberBandZoomModifier);

    this.zoomPanModifier = new ZoomPanModifier();
    this.zoomPanModifier.isEnabled = false;
    sciChartSurface.chartModifiers.add(this.zoomPanModifier);

    // Create a line series with some data
    this.xyDataSeries = new XyDataSeries(wasmContext);

    const lineSeries = new FastLineRenderableSeries(wasmContext, {
      stroke: '#ffffff',
      strokeThickness: 2,
      dataSeries: this.xyDataSeries,
    });
    lineSeries.rolloverModifierProps.tooltipColor = '#F3FEF8';
    lineSeries.rolloverModifierProps.tooltipTextColor = '#3B3D3C';
    sciChartSurface.renderableSeries.add(lineSeries);

    const annt_liner_start = new VerticalLineAnnotation({
      labelPlacement: ELabelPlacement.Axis,
      showLabel: true,
      stroke: '#24A948',
      strokeThickness: 3,
      x1: 0,
      axisLabelFill: '#24A948',
      axisFontSize: 14,
      isEditable: false,
      isSelected: false,
    });

    const annt_liner_end = new VerticalLineAnnotation({
      labelPlacement: ELabelPlacement.Axis,
      showLabel: true,
      stroke: '#24A948',
      strokeThickness: 3,
      x1: 0,
      axisLabelFill: '#24A948',
      axisFontSize: 14,
      isEditable: false,
      isSelected: false,
    });

    sciChartSurface.annotations.add(annt_liner_start);
    sciChartSurface.annotations.add(annt_liner_end);

    this.faultBoxAnnotation = new BoxAnnotation({
      fill: '#ED9311',
      stroke: '#ED9311',
      strokeThickness: 1,
      x1: 0,
      x2: 0,
      y1: 0,
      y2: 0,
      opacity: 0.5,
    });

    this.selectedProjectSubscription =
      this.projectService.selectedProject.subscribe(
        (data) => {
          if (this.xyDataSeries && this.xyDataSeries.count() > 0) {
            this.xyDataSeries.clear();
          }

          this.mainFaultType = 0;
          this.cmsFaultType = 0;

          if (this.FD && this.FD.length > 0) {
            this.FD = this.defaultFaults;
          }
          if (this.FP && this.FP.length > 0) {
            this.FP = this.defaultFaults;
          }
          if (this.FM && this.FM.length > 0) {
            this.FM = this.defaultFaults;
          }
          console.log(
            'Health > projectService.selectedProject.subscribe. proj: ' + data
          );
          // console.log(
          //   'selected proj change >> dataloaded: ' + this.tempDataLoaded
          // );
        },
        (error) => (this.errorMessage = error)
      );

    // Unsubscribe from previous subscriptions if they exist to prevent memory leaks
    if (this.tempDataSubscription && typeof this.tempDataSubscription.unsubscribe === 'function') {
      this.tempDataSubscription.unsubscribe();
    }
    this.tempDataSubscription = this.projectService.lossData.subscribe(
      (data) => {
        this.lossData = data;
        this.lossDataRows = data.LRC;
        this.samplingInterval = data.SI;
        this.channelrange_start = data.CRB;
        this.channelrange_end = data.CRE;
        this.channelrange_span = data.CRS;

        if (data == null) {
          return;
        } else if (data.LRC == null) {
          return;
        } else if (data.LRC.length == 0) {
          return;
        } else if (data.LRC[0].LR == null) {
          return;
        } else if (data.LRC[0].LR.length == 0) {
          return;
        }

        console.log('loss data received');

        //console.log('this.channelrange_end: ' + this.channelrange_end);

        var displayedLength =
          (this.lossDataRows[0].LR.length - this.lossData.LSS) *
          this.lossData.SI;
        console.log('Length:' + displayedLength);

        annt_liner_end.x1 = displayedLength;
        annt_liner_start.x1 =
          displayedLength - (this.lossData.CRE - this.lossData.CRB);

        this.updateTempChartForTick(sciChartSurface, '0');
        sciChartSurface.zoomExtents();
        yAxis.visibleRange = new NumberRange(-1, 1);
        // if (yAxis.visibleRange.max <= 0) {
        //   var newMin = yAxis.visibleRange.min;
        //   var roundedMin = Math.round(newMin);
        //   if (roundedMin > newMin) {
        //     newMin = roundedMin - 0.5;
        //   } else {
        //     newMin = roundedMin - 0.4;
        //   }

        //   // console.log(
        //   //   '2: visible range updated to 5, min:' +
        //   //     newMin +
        //   //     ' rounded:' +
        //   //     roundedMin
        //   // );

        //   var newMax = 0;
        //   if (yAxis.visibleRange.max > -0.4) {
        //     newMax = yAxis.visibleRange.max + 0.4;
        //     // console.log(
        //     //   'cur max:' + yAxis.visibleRange.max + ' newMax:' + newMax
        //     // );
        //   }

        //   yAxis.visibleRange = new NumberRange(newMin, newMax);
        // }
      },
      (error) => (this.errorMessage = error)
    );

    // Unsubscribe from previous subscription if it exists to prevent memory leaks
    if (this.tempRTDataSubscription && typeof this.tempRTDataSubscription.unsubscribe === 'function') {
      this.tempRTDataSubscription.unsubscribe();
    }
    this.tempRTDataSubscription = this.projectService.lossRTData.subscribe(
      (data) => {
        if (data == null) {
          return;
        } else if (data.LRC == null) {
          return;
        } else if (data.LRC.length == 0) {
          return;
        } else if (data.LRC[0].LR == null) {
          return;
        } else if (data.LRC[0].LR.length == 0) {
          return;
        }

        console.log('loss RT data received');

        this.lossDataRows.push(data.LRC[0]);

        var lastPos = this.lossDataRows.length - 1;
        this.updateTempChartForTick(sciChartSurface, lastPos.toString());

        // sciChartSurface.zoomExtents();
        // if (yAxis.visibleRange.max <= 0) {
        //   var newMin = yAxis.visibleRange.min;
        //   var roundedMin = Math.round(newMin);
        //   if (roundedMin > newMin) {
        //     newMin = roundedMin - 0.5;
        //   } else {
        //     newMin = roundedMin - 0.4;
        //   }

        //   // console.log(
        //   //   '2: visible range updated to 5, min:' +
        //   //     newMin +
        //   //     ' rounded:' +
        //   //     roundedMin
        //   // );

        //   var newMax = 0;
        //   if (yAxis.visibleRange.max > -0.4) {
        //     newMax = yAxis.visibleRange.max + 0.4;
        //     // console.log(
        //     //   'cur max:' + yAxis.visibleRange.max + ' newMax:' + newMax
        //     // );
        //   }

        //   yAxis.visibleRange = new NumberRange(newMin, newMax);
        // }
      },
      (error) => (this.errorMessage = error)
    );

    // this.tickSubscription = this.projectService.tickPosition.subscribe(
    //   (data) => {
    //     // console.log(
    //     //   'projectService.tickPosition.subscribe. Tick: ' + data.toString()
    //     // );

    //     this.updateTempChartForTick(sciChartSurface, data.toString());
    //     sciChartSurface.zoomExtents();

    //     // console.log(
    //     //   'min:' + yAxis.visibleRange.min + ' max:' + yAxis.visibleRange.max
    //     // );
    //     if (yAxis.visibleRange.max <= 0) {
    //       var newMin = yAxis.visibleRange.min;
    //       var roundedMin = Math.round(newMin);
    //       if (roundedMin > newMin) {
    //         newMin = roundedMin - 0.5;
    //       } else {
    //         newMin = roundedMin - 0.4;
    //       }

    //       // console.log(
    //       //   '2: visible range updated to 5, min:' +
    //       //     newMin +
    //       //     ' rounded:' +
    //       //     roundedMin
    //       // );

    //       var newMax = 0;
    //       if (yAxis.visibleRange.max > -0.4) {
    //         newMax = yAxis.visibleRange.max + 0.4;
    //         // console.log(
    //         //   'cur max:' + yAxis.visibleRange.max + ' newMax:' + newMax
    //         // );
    //       }

    //       yAxis.visibleRange = new NumberRange(newMin, newMax);
    //     }
    //   },
    //   (error) => (this.errorMessage = error)
    // );

    // Listen to Control Events:
    this.onZoomOut = () => {
      console.log(this.channelrange_start + '-' + this.channelrange_end);
      // xAxis.visibleRange = new NumberRange(
      //   this.channelrange_start,
      //   this.channelrange_end
      // );
      sciChartSurface.zoomExtents();
    };

    this.onAddMarker = () => {
      this.projectService.addMarker();
    };
  }

  formatxAxisTemperatureGraph(dataValue: number) {
    //adjust the value that user sees on the axis from 0 to end of range instead of from startRange to endRange.
    return (
      (dataValue - this.channelrange_start).toFixed(2) +
      (this.projectService.isMetricUnit ? 'm' : 'ft')
    );
  }

  updateTempChartForTick(sciChartSurface: SciChartSurface, val: string) {
    const sliderVal: number = +val;
    this.tick = sliderVal;

    // Update Temp Graph
    if (this.lossDataLoaded) {
      this.xyDataSeries.clear();
      //console.log('dataLoaded:true. Cleared Series.');
    }

    this.FD = this.defaultFaults;
    this.FP = this.defaultFaults;
    this.FM = this.defaultFaults;

    if (this.lossDataRows.length == 0) {
      this.lossDataLoaded = false;
      return;
    } else {
      this.lossDataLoaded = true;
    }

    // Update Faults
    if (this.lossDataRows[sliderVal].FD.length > 0) {
      this.FD = this.lossDataRows[sliderVal].FD;
    }
    if (this.lossDataRows[sliderVal].FP.length > 0) {
      this.FP = this.lossDataRows[sliderVal].FP;
    }
    if (this.lossDataRows[sliderVal].FM.length > 0) {
      this.FM = this.lossDataRows[sliderVal].FM;
    }

    if (this.FM.some((c) => c.FT == '2')) {
      this.mainFaultType = 2;
      this.projectService.setFiberFaultLevel(2);
    } else if (this.FM.some((c) => c.FT == '1')) {
      this.mainFaultType = 1;
      this.projectService.setFiberFaultLevel(1);
    } else {
      this.mainFaultType = 0;
      this.projectService.setFiberFaultLevel(0);
    }

    //var hasCMSFaults = false;
    if (this.FD.some((c) => c.FT == '2')) {
      //hasCMSFaults = true;
      this.cmsFaultType = 2;
      this.projectService.setCmsFaultLevel(2);
    } else if (this.FD.some((c) => c.FT == '1')) {
      //hasCMSFaults = true;
      this.cmsFaultType = 1;
      this.projectService.setCmsFaultLevel(1);
    } else {
      this.cmsFaultType = 0;
      this.projectService.setCmsFaultLevel(0);
    }

    // Calculations for Displayed Points and Data -----------

    const startDataPoint = this.lossData.MIR ? this.lossData.LSS : 0;
    const endDataPoint = this.lossData.MIR
      ? this.lossDataRows[sliderVal].LR.length
      : this.lossData.LES;
    const displayedPoints = endDataPoint - startDataPoint;

    var xValues: number[] = new Array(displayedPoints);
    var yValues: number[] = new Array(displayedPoints);

    // X Factor = (Liner Length + Device Length?) / Data Points Count

    var displayedLength = displayedPoints * this.lossData.SI;

    console.log('Slider Val:' + sliderVal);

    console.log(
      'Total Data Points:' +
        this.lossDataRows[sliderVal].LR.length +
        '  LSS:' +
        startDataPoint +
        '  LES:' +
        endDataPoint
    );

    var totalLength = this.lossDataRows[sliderVal].LR.length * this.lossData.SI;

    console.log(
      'totalLength:' + totalLength + ' IsMirror:' + this.lossData.MIR
    );
    console.log(
      'displayedLength:' +
        displayedLength +
        '  displayedPoints:' +
        displayedPoints
    );
    const xFactor = displayedLength / displayedPoints;

    for (let j = startDataPoint; j < endDataPoint; j++) {
      xValues[j - startDataPoint] = (j - startDataPoint) * xFactor;
      yValues[j - startDataPoint] = this.lossDataRows[sliderVal].LR[j];
    }

    if (this.lossData.MIR) {
      this.xyDataSeries.appendRange(xValues, yValues.reverse());
    } else {
      this.xyDataSeries.appendRange(xValues, yValues);
    }

    //this.clearBoxAnnotations(sciChartSurface);

    // if (this.FD.length > 0) {
    //   this.FD.forEach((fd, index) => {
    //     console.log('Faulttype:' + fd.FT + ' SP:' + fd.PS + ' SE:' + fd.PE);
    //     this.createBoxArea(
    //       sciChartSurface,
    //       xValues[fd.PS],
    //       xValues[fd.PE],
    //       yValues[fd.PS],
    //       yValues[fd.PE],
    //       fd.V,
    //       fd.FT
    //     );
    //   });
    // }

    // if (this.FP.length > 0) {
    //   this.FP.forEach((fp, index) => {
    //     console.log('Faulttype:' + fp.FT + ' SP:' + fp.PS + ' SE:' + fp.PE);
    //     this.createBoxArea(
    //       sciChartSurface,
    //       xValues[fp.PS],
    //       xValues[fp.PE],
    //       yValues[fp.PS],
    //       yValues[fp.PE],
    //       fp.V,
    //       fp.FT
    //     );
    //   });
    // }
    // if (this.FM.length > 0) {
    //   this.FM.forEach((fm, index) => {
    //     console.log('Faulttype:' + fm.FT + ' SP:' + fm.PS + ' SE:' + fm.PE);
    //     this.createBoxArea(
    //       sciChartSurface,
    //       xValues[fm.PS],
    //       xValues[fm.PE],
    //       yValues[fm.PS],
    //       yValues[fm.PE],
    //       fm.V,
    //       fm.FT
    //     );
    //   });
    // }
  }

  clearBoxAnnotations(sciChartSurface: SciChartSurface) {
    for (let a = sciChartSurface.annotations.size() - 1; a >= 0; a--) {
      if (
        sciChartSurface.annotations.get(a).type !=
        EAnnotationType.RenderContextVerticalLineAnnotation
      ) {
        sciChartSurface.annotations.removeAt(a);
      }
    }
  }

  createBoxArea(
    sciChartSurface: SciChartSurface,
    x1Val: number,
    x2Val: number,
    y1Val: number,
    y2Val: number,
    faultVal: number,
    faultType: string
  ) {
    var boxColor = 'black';
    if (faultType == '1') {
      boxColor = '#F29611';
    } else if (faultType == '2') {
      boxColor = '#F7300D';
    } else {
      return;
    }

    var ann = new BoxAnnotation({
      fill: boxColor,
      stroke: boxColor,
      strokeThickness: 1,
      x1: x1Val,
      x2: x2Val,
      y1: y1Val,
      y2: y2Val,
      opacity: 0.5,
    });
    sciChartSurface.annotations.add(ann);
    var tAnn = new TextAnnotation({
      text: faultVal.toFixed(2) + ' dB',
      fontSize: 16,
      x1: x2Val,
      y1: y1Val,
    });
    sciChartSurface.annotations.add(tAnn);
  }

  updateBoxArea(
    sciChartSurface: SciChartSurface,
    show: Boolean,
    annot: BoxAnnotation,
    x1Val: number,
    x2Val: number,
    y1Val: number,
    y2Val: number
  ) {
    if (show) {
      annot.x1 = x1Val;
      annot.x2 = x2Val;
      annot.y1 = y1Val;
      annot.y2 = y2Val;
      if (!sciChartSurface.annotations.contains(annot)) {
        sciChartSurface.annotations.add(annot);
      }
    } else {
      if (sciChartSurface.annotations.contains(annot)) {
        sciChartSurface.annotations.remove(annot);
      }
    }
  }

  getFaultStatusColor(faultType: string): string {
    if (faultType == '0') {
      return 'green';
    } else if (faultType == '1') {
      return 'yellow';
    } else if (faultType == '2' || faultType == '3') {
      return 'red';
    } else {
      return 'green';
    }
  }

  calculateDistance(position: number, samplingInterval: number): number {
    if (samplingInterval > 0) {
      return position * samplingInterval;
    } else {
      return position;
    }
  }

  setRollOver(val: boolean) {
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = false;
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = false;
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = val;
  }
  setPan(val: boolean) {
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = false;
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = false;
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = val;
  }
  setZoom(val: boolean) {
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = false;
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = false;
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = val;
  }

  zoomOut() {
    if (!this.onZoomOut) return;
    this.onZoomOut();
  }

  addMarker() {
    if (!this.onAddMarker) return;
    this.onAddMarker();
  }

  toggleHelpOverlay() {
    this.projectService.isHelpOverlayVisible =
      !this.projectService.isHelpOverlayVisible;
  }
}
